// ===================================================================
// KAIXO - LatidoDoradoBar
// ===================================================================
// Termómetro bajo cada post. Si NO está desbloqueado aún, muestra
// barra de progreso hacia el umbral. Si SÍ está desbloqueado, muestra
// el card con los slots disponibles + botón "Dame el Latido Dorado".
// ===================================================================

import React, { useState } from 'react';
import useLatidoDorado from '../../hooks/useLatidoDorado';
import LatidoDoradoAnimation from './LatidoDoradoAnimation';
import './LatidoDorado.css';

function formatCountdown(expiresAt) {
    if (!expiresAt) return '';
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return 'Caducado';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h > 0) return `Vence en ${h}h ${m}m`;
    return `Vence en ${m}m`;
}

// Cuenta likes (❤️, ♥️, 👍) de un objeto reactions, matcheando por
// codepoint base para cubrir cualquier variante Unicode.
function countLikesFromReactions(reactions) {
    if (!reactions || typeof reactions !== 'object') return null;
    let total = 0;
    for (const [k, users] of Object.entries(reactions)) {
        if (!k || !Array.isArray(users)) continue;
        const cp = k.codePointAt(0);
        if (cp === 0x2764 || cp === 0x2665 || cp === 0x1F44D) total += users.length;
    }
    return total;
}

function LatidoDoradoBar({ postId, reactions }) {
    const { progress, loading, claim, claiming, error } = useLatidoDorado(postId);
    const [animData, setAnimData] = useState(null);

    if (loading || !progress) return null;

    // Si el feed nos pasa las reacciones en vivo, contamos desde ahí
    // para que la barra se mueva al instante sin esperar al backend.
    // El backend sigue siendo fuente de verdad para threshold/unlock.
    const liveLikes = countLikesFromReactions(reactions);

    // Estado 1: aún no desbloqueado — termómetro
    if (!progress.unlocked) {
        const currentLikes = liveLikes != null ? liveLikes : progress.currentLikes;
        const pct = Math.min(100, (currentLikes / progress.thresholdLikes) * 100);
        return (
            <div className="ld-bar-wrapper">
                <div className="ld-bar-header">
                    <span>💛 Latido Dorado</span>
                    <span>{currentLikes} / {progress.thresholdLikes}</span>
                </div>
                <div className="ld-bar-track">
                    <div className="ld-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="ld-bar-status">
                    Desbloquea 10 Latidos Dorados cuando lleguéis a {progress.thresholdLikes} likes
                </div>
            </div>
        );
    }

    // Estado 2: desbloqueado — card
    const slot = progress.slot;
    const goldTaken   = 3 - slot.goldLeft;
    const silverTaken = 7 - slot.silverLeft;
    const available = slot.goldLeft + slot.silverLeft;

    const handleClaim = async () => {
        const result = await claim();
        if (result?.kind) {
            const rank = result.kind === 'gold'
                ? (3 - slot.goldLeft + 1)
                : (3 + (7 - slot.silverLeft) + 1);
            setAnimData({ kind: result.kind, rank });
        }
    };

    return (
        <>
            <div className="ld-unlocked-card">
                <div className="ld-unlocked-title">🎉 ¡Latido Dorado desbloqueado!</div>
                <div className="ld-slots-row">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <span key={`g${i}`} className={`ld-slot-medal gold ${i < goldTaken ? 'taken' : ''}`}>🥇</span>
                    ))}
                    {Array.from({ length: 7 }).map((_, i) => (
                        <span key={`s${i}`} className={`ld-slot-medal silver ${i < silverTaken ? 'taken' : ''}`}>🥈</span>
                    ))}
                </div>
                <div className="ld-countdown">
                    ⏱ {formatCountdown(slot.expiresAt)} · {available} disponibles
                </div>
                <button
                    className="ld-claim-btn"
                    onClick={handleClaim}
                    disabled={claiming || available === 0}
                >
                    {claiming ? 'Reclamando...' : available === 0 ? 'Agotados' : 'Dame el Latido Dorado'}
                </button>
                {error && <div style={{ fontSize: 11, color: '#E53935', marginTop: 6 }}>{error}</div>}
            </div>
            {animData && (
                <LatidoDoradoAnimation
                    kind={animData.kind}
                    rank={animData.rank}
                    onDone={() => setAnimData(null)}
                />
            )}
        </>
    );
}

export default LatidoDoradoBar;
